// localStorage-backed data store for BizDocs Manager (mock persistence layer)
import { COMPANY, CUSTOMERS, PRODUCTS, QUOTATIONS, INVOICES, RECEIPTS, VOUCHERS, PROPOSALS, USERS, CASHFLOW } from '../mock';

const KEY = 'bizdocs_state_v1';

const seed = () => ({
  company: COMPANY,
  customers: CUSTOMERS,
  products: PRODUCTS,
  quotations: QUOTATIONS,
  invoices: INVOICES,
  receipts: RECEIPTS,
  vouchers: VOUCHERS,
  proposals: PROPOSALS,
  users: USERS,
  cashflow: CASHFLOW,
});

export const loadState = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      const s = seed();
      localStorage.setItem(KEY, JSON.stringify(s));
      return s;
    }
    return JSON.parse(raw);
  } catch (e) {
    return seed();
  }
};

export const saveState = (state) => {
  localStorage.setItem(KEY, JSON.stringify(state));
};

export const resetState = () => {
  localStorage.removeItem(KEY);
};

// Helpers
export const fmtRM = (n) => 'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const fmtRM2 = (n) => 'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtDate = (iso) => {
  if (!iso) return '-';
  const dt = new Date(iso);
  return dt.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const genId = () => Math.random().toString(36).slice(2, 10);

// Reference-number generation. Format: PREFIX-YYYY-0001 (year optional), padding configurable,
// and an optional manual "next number" override per document type.
const PREFIX_KEYS = {
  quotations: 'quotationPrefix', invoices: 'invoicePrefix', receipts: 'receiptPrefix',
  vouchers: 'voucherPrefix', proposals: 'proposalPrefix',
};

export const NUMBER_TYPES = [
  { key: 'quotations', label: 'Quotation', prefixKey: 'quotationPrefix' },
  { key: 'invoices', label: 'Invoice', prefixKey: 'invoicePrefix' },
  { key: 'receipts', label: 'Receipt', prefixKey: 'receiptPrefix' },
  { key: 'vouchers', label: 'Payment Voucher', prefixKey: 'voucherPrefix' },
  { key: 'proposals', label: 'Proposal', prefixKey: 'proposalPrefix' },
];

export const seqOf = (number) => {
  const m = String(number || '').match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : 0;
};

export const buildNumber = (company, type, existing = [], dateISO) => {
  const prefix = (company[PREFIX_KEYS[type]] || 'DOC').trim();
  const pad = Math.min(8, Math.max(1, Number(company.numberPadding) || 4));
  const includeYear = company.includeYear !== false;
  const year = (dateISO ? new Date(dateISO) : new Date()).getFullYear();

  const manual = Number(company.nextNumbers?.[type]);
  let seq;
  if (manual > 0) {
    seq = manual;
  } else {
    let max = 0;
    existing.forEach((doc) => {
      const num = String(doc.number || '');
      if (includeYear && !num.includes(`-${year}-`)) return;
      max = Math.max(max, seqOf(num));
    });
    seq = max + 1;
  }
  const seqStr = String(seq).padStart(pad, '0');
  return includeYear ? `${prefix}-${year}-${seqStr}` : `${prefix}-${seqStr}`;
};

// Compute document totals
export const computeTotals = (items, sstRate = 6) => {
  let subtotal = 0;
  let tax = 0;
  (items || []).forEach((it) => {
    const line = (Number(it.qty) || 0) * (Number(it.price) || 0);
    subtotal += line;
    if (it.taxable) tax += line * (sstRate / 100);
  });
  return { subtotal, tax, total: subtotal + tax };
};
